import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Mail, Send, ShieldCheck, Server, Lock, CheckCircle2, XCircle, ImagePlus, Trash2, PenLine, Code, RotateCcw, Copy } from "lucide-react";

const emptyForm = {
  host: "",
  port: 587,
  username: "",
  password: "",
  from_email: "",
  from_name: "Support Team",
  encryption: "starttls",
  enabled: true,
};

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "");

const SmtpDeliveryStatus = ({ status }) => {
  const errNewer = status.last_error_at && (!status.last_success_at || status.last_error_at > status.last_success_at);
  if (!status.last_error_at && !status.last_success_at) return null;
  return (
    <div className="mt-4 space-y-1 text-xs" data-testid="smtp-delivery-status">
      {status.last_success_at && (
        <div className="text-emerald-700" data-testid="smtp-last-success">
          Last successful send: {fmtTs(status.last_success_at)}
        </div>
      )}
      {errNewer && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700" data-testid="smtp-last-error">
          Last failed send ({fmtTs(status.last_error_at)}): {status.last_error}
        </div>
      )}
    </div>
  );
};


export default function AdminSettings() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Email signature / footer
  const [footer, setFooter] = useState({ text: "", signature_html: "", enabled: true, has_logo: false, logo_data_url: null, logo_public_url: "" });
  const [footerHtml, setFooterHtml] = useState("");
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [footerSaving, setFooterSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const htmlTaRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/smtp");
      setStatus(data);
      if (data.configured) {
        setForm({
          host: data.host || "",
          port: data.port || 587,
          username: data.username || "",
          password: "",
          from_email: data.from_email || "",
          from_name: data.from_name || "Support Team",
          encryption: data.encryption || "starttls",
          enabled: data.enabled ?? true,
        });
      }
    } catch (err) {
      toast.error("Could not load settings", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadFooter = async () => {
    try {
      const { data } = await api.get("/settings/email-footer");
      setFooter(data);
      setFooterHtml(data.signature_html || "");
      setFooterEnabled(data.enabled ?? true);
    } catch (err) {
      toast.error("Could not load signature", { description: formatApiError(err) });
    }
  };

  useEffect(() => { loadFooter(); }, []);

  const saveFooter = async () => {
    setFooterSaving(true);
    try {
      const { data } = await api.put("/settings/email-footer", {
        text: footer.text || "",
        signature_html: footerHtml,
        enabled: footerEnabled,
      });
      setFooter(data);
      setFooterHtml(data.signature_html || "");
      toast.success("Email signature saved");
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setFooterSaving(false);
    }
  };

  const insertAtCursor = (snippet) => {
    const ta = htmlTaRef.current;
    if (!ta) { setFooterHtml((v) => v + snippet); return; }
    const start = ta.selectionStart ?? footerHtml.length;
    const end = ta.selectionEnd ?? footerHtml.length;
    const next = footerHtml.slice(0, start) + snippet + footerHtml.slice(end);
    setFooterHtml(next);
    // Move caret after inserted content
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + snippet.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const insertLogo = () => {
    const url = footer.logo_public_url;
    if (!url) {
      toast.error("Upload a logo first, then use this button.");
      return;
    }
    insertAtCursor(
      `<img src="${url}" alt="Logo" style="display:block;margin-bottom:10px;max-height:48px;height:auto;border:0;" />\n`
    );
  };

  const resetToDefault = () => {
    if (!window.confirm("Replace your current signature HTML with the built-in default?")) return;
    const url = footer.logo_public_url || "";
    const img = url
      ? `<img src="${url}" alt="Pixel Informatics" style="display:block;margin-bottom:10px;max-height:48px;height:auto;border:0;" />\n`
      : "";
    const html = (
      `<div style="margin-top:24px;padding-top:14px;border-top:1px solid #e5e7eb;` +
      `font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;color:#334155;">\n` +
      `${img}` +
      `<div><b>Pixel Informatics (P) Ltd</b></div>\n` +
      `<div>7A Hospital Street | Second Floor | Kolkata - 700072 | India</div>\n` +
      `<div>Tel: +91 9903951191 | +91 9163166623</div>\n` +
      `</div>`
    );
    setFooterHtml(html);
  };

  const copyLogoUrl = async () => {
    if (!footer.logo_public_url) return;
    try {
      await navigator.clipboard.writeText(footer.logo_public_url);
      toast.success("Logo URL copied");
    } catch {
      toast.error("Copy failed", { description: footer.logo_public_url });
    }
  };

  const onLogoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file re-triggers
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error("Logo too large", { description: "Please choose an image under 512KB." });
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/email-footer/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFooter(data);
      toast.success("Logo updated");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the signature logo? Existing signatures that reference the logo URL will show a broken image until you update or re-upload.")) return;
    try {
      const { data } = await api.delete("/settings/email-footer/logo");
      setFooter(data);
      toast.success("Logo removed");
    } catch (err) {
      toast.error("Remove failed", { description: formatApiError(err) });
    }
  };
  // Not currently wired to a UI action — kept for potential future use.
  void removeLogo;

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
      : e.target.type === "number" ? Number(e.target.value)
      : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // if we've already saved and password field left blank, omit to preserve existing
      if (status.configured && !payload.password) delete payload.password;
      const { data } = await api.put("/settings/smtp", payload);
      setStatus(data);
      toast.success("SMTP settings saved");
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      await api.post("/settings/smtp/test", { to_email: testEmail });
      toast.success("Test email sent", { description: `Delivered to ${testEmail}` });
    } catch (err) {
      toast.error("Test failed", { description: formatApiError(err) });
    } finally {
      setTesting(false);
      api.get("/settings/smtp").then(({ data }) => setStatus(data)).catch(() => {});
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500 text-sm">Loading settings...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Email settings
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Connect your SMTP server so agent replies get emailed straight to customers.
          </p>
        </div>
        <div
          className={`beacon-badge ${
            status.configured && status.enabled
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-slate-100 text-slate-600 border-slate-200"
          }`}
          data-testid="smtp-status-badge"
        >
          {status.configured && status.enabled ? (
            <><CheckCircle2 className="w-3.5 h-3.5" /> Active</>
          ) : status.configured ? (
            <><XCircle className="w-3.5 h-3.5" /> Disabled</>
          ) : (
            <>Not configured</>
          )}
        </div>
      </div>

      {status.password_unreadable && (
        <div data-testid="smtp-password-unreadable-alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Stored SMTP password could not be decrypted. Please re-enter the password below and save.
        </div>
      )}

      <form onSubmit={save} className="beacon-card p-6 sm:p-7 space-y-5 shadow-sm">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
          <Server className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Server</div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="beacon-label">SMTP host</label>
            <input required className="beacon-input" placeholder="smtp.yourdomain.com"
              data-testid="smtp-host-input"
              value={form.host} onChange={upd("host")} />
          </div>
          <div>
            <label className="beacon-label">Port</label>
            <input required type="number" min={1} max={65535} className="beacon-input"
              data-testid="smtp-port-input"
              value={form.port} onChange={upd("port")} />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="beacon-label">Encryption</label>
            <select className="beacon-input" data-testid="smtp-encryption-select"
              value={form.encryption} onChange={upd("encryption")}>
              <option value="starttls">STARTTLS (port 587)</option>
              <option value="ssl">SSL / TLS (port 465)</option>
              <option value="none">None (plain — not recommended)</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
              <input type="checkbox" data-testid="smtp-enabled-toggle"
                checked={form.enabled} onChange={upd("enabled")}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              Enabled
            </label>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 pb-2 border-b border-slate-200">
          <Lock className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Authentication</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="beacon-label">Username</label>
            <input className="beacon-input" placeholder="apikey / your login"
              data-testid="smtp-username-input"
              value={form.username} onChange={upd("username")} />
          </div>
          <div>
            <label className="beacon-label">
              Password {status.has_password && <span className="text-slate-400 font-normal">(leave blank to keep saved)</span>}
            </label>
            <input type="password" className="beacon-input"
              placeholder={status.has_password ? "•••••••• (unchanged)" : "SMTP password"}
              data-testid="smtp-password-input"
              value={form.password} onChange={upd("password")} />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 pb-2 border-b border-slate-200">
          <Mail className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Sender identity</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="beacon-label">From email</label>
            <input required type="email" className="beacon-input" placeholder="support@yourdomain.com"
              data-testid="smtp-from-email-input"
              value={form.from_email} onChange={upd("from_email")} />
          </div>
          <div>
            <label className="beacon-label">From name</label>
            <input className="beacon-input" placeholder="Support Team"
              data-testid="smtp-from-name-input"
              value={form.from_name} onChange={upd("from_name")} />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Credentials are stored on your own MongoDB — never shared externally.
          </div>
          <Button type="submit" disabled={saving} data-testid="smtp-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </div>
      </form>

      <div className="beacon-card p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Send className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Send a test email</div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Fires a small message using the currently saved settings.
        </p>
        <div className="flex gap-2 flex-wrap">
          <input type="email" className="beacon-input flex-1 min-w-[220px]" placeholder="you@example.com"
            data-testid="smtp-test-email-input"
            value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          <Button onClick={runTest} disabled={testing || !testEmail || !status.configured}
            data-testid="smtp-test-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
            {testing ? "Sending..." : "Send test"}
          </Button>
        </div>
        {!status.configured && (
          <div className="mt-2 text-xs text-slate-500">Save your SMTP settings first.</div>
        )}
        <SmtpDeliveryStatus status={status} />
      </div>
      <div className="beacon-card p-6 shadow-sm" data-testid="email-signature-card">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Email signature / footer</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Appended to every outgoing email (replies, ticket acknowledgements, subscription reminders).
          Compose in HTML — the logo is served over HTTPS so mail clients render it inline (never as an attachment).
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Editable side */}
          <div className="space-y-4">
            <div>
              <label className="beacon-label">Logo</label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-16 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden shrink-0"
                     data-testid="footer-logo-thumb">
                  {footer.has_logo && footer.logo_data_url ? (
                    <img src={footer.logo_data_url} alt="Signature logo"
                         className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-slate-400 font-mono uppercase tracking-widest">no logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                    className="hidden"
                    data-testid="footer-logo-file-input"
                    onChange={onLogoPicked}
                  />
                  <Button type="button" size="sm" variant="outline" disabled={logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                    data-testid="footer-logo-upload-btn">
                    <ImagePlus className="w-3.5 h-3.5 mr-1.5" />
                    {logoUploading ? "Uploading..." : (footer.has_logo ? "Replace logo" : "Upload logo")}
                  </Button>
                  {footer.has_logo && footer.logo_public_url && (
                    <button type="button" onClick={copyLogoUrl}
                      data-testid="footer-logo-copy-url"
                      className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1 self-start">
                      <Copy className="w-3 h-3" /> Copy logo URL
                    </button>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-slate-400 mt-2">PNG / JPG / SVG · under 512KB · served over HTTPS in emails.</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="beacon-label mb-0">Signature HTML</label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" size="sm" variant="ghost"
                    onClick={insertLogo}
                    disabled={!footer.has_logo}
                    data-testid="footer-insert-logo-btn"
                    className="h-7 px-2 text-[11px]">
                    <ImagePlus className="w-3 h-3 mr-1" /> Insert logo
                  </Button>
                  <Button type="button" size="sm" variant="ghost"
                    onClick={resetToDefault}
                    data-testid="footer-reset-btn"
                    className="h-7 px-2 text-[11px]">
                    <RotateCcw className="w-3 h-3 mr-1" /> Reset default
                  </Button>
                </div>
              </div>
              <textarea rows={12} ref={htmlTaRef}
                className="beacon-input resize-y font-mono text-[11px] leading-relaxed"
                data-testid="footer-html-input"
                spellCheck={false}
                placeholder='<div>Your name</div>&#10;<div>Company</div>&#10;<img src="https://…/logo.png" style="max-height:48px" />'
                value={footerHtml} onChange={(e) => setFooterHtml(e.target.value)} />
              <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
                <Code className="w-3 h-3" /> Any valid email-safe HTML. Use inline styles (many clients ignore &lt;style&gt; blocks).
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={footerEnabled}
                onChange={(e) => setFooterEnabled(e.target.checked)}
                data-testid="footer-enabled-toggle"
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              Attach this signature to every outgoing email
            </label>

            <div className="flex justify-end">
              <Button type="button" onClick={saveFooter} disabled={footerSaving}
                data-testid="footer-save-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg">
                {footerSaving ? "Saving..." : "Save signature"}
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Preview</div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="footer-preview">
              <div className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap">
                Hi Alex,
                {"\n\n"}Thanks for reaching out — we're on it and will follow up shortly.
                {"\n\n"}— Support Team
              </div>
              {footerEnabled && footerHtml.trim() ? (
                <div className="mt-2" dangerouslySetInnerHTML={{ __html: footerHtml }} />
              ) : !footerEnabled ? (
                <div className="mt-4 text-[11px] text-slate-400 italic">Signature is disabled — outgoing emails won't include it.</div>
              ) : (
                <div className="mt-4 text-[11px] text-slate-400 italic">Signature HTML is empty.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
